import type { AlbumLibraryEntry, DeepDive } from '@/types/album';

export const library = {
  id: 'lateralus',
  artist: 'Tool',
  album: 'Lateralus',
  year: 2001,
  genre: 'Metal progresivo',
  hook: 'Una iniciación por umbrales que va de la rabia al trance.',
  palette: { bg: '#0A0E12', fg: '#C8D4DA', accent: '#5FA0B8' },
  typeStyle: 'wide-display',
  available: true,
  hero: {
    apparent: 'una demostración de fuerza cerebral',
    key: 'un ritual de vaciado',
    coda: 'donde la complejidad sirve para llevarte de la rabia al trance.',
  },
  deepDiveLine: {
    apparent: 'una obra mayor de metal progresivo',
    key: 'una iniciación por etapas que disuelve la identidad en pulso, repetición y ritual',
  },
  sectionTitle: {
    pre: 'Lateralus, leído como una',
    em: 'iniciación por umbrales',
    post: 'que va de la rabia al trance.',
  },
  songsTitle: 'Tres canciones, leídas con cuidado.',
  footerCredit: 'Análisis original · Lateralus · Tool · 2001',
  coverImage: '/images/lateralus.jpg',
} as const satisfies AlbumLibraryEntry;

export const deepDive: DeepDive = {
  artist: 'Tool',
  title: 'Lateralus',
  year: '2001',
  position_label: 'Culminación expansiva · Tercer álbum',
  apparent_form:
    'Un álbum de metal progresivo grande, técnico y muy secuenciado, construido como declaración espiritual y musical de una banda que quiere hacer su obra mayor.',
  actual_behavior:
    'Escuchado entero, el disco funciona como una iniciación por etapas. Arranca en confrontación física, abre espacios de suspensión en los interludios, alcanza su centro en la búsqueda de "Lateralus" y luego entra en un trance largo donde la identidad individual se disuelve en pulso, repetición y ritual antes de cerrar con una intrusión paranoica que rompe el estado conseguido.',
  form_behavior_gap:
    'Parece una demostración de fuerza cerebral; se mueve como un ritual de vaciado donde la complejidad sirve para llevarte de la rabia al trance.',
  previous_context:
    '"Ænima" era más cínico, más urbano y más inclinado al choque entre violencia, sarcasmo y asco; "Lateralus" conserva la intensidad pero cambia la dirección hacia paciencia, repetición y búsqueda espiritual.',
  next_context:
    '"10,000 Days" tomó parte de esta amplitud y la hizo más fragmentada y terrenal, con canciones muy elaboradas pero menos unificadas por un solo impulso de ascenso.',
  previous_album: { title: 'Ænima', year: 1996 },
  next_album: { title: '10,000 Days', year: 2006 },
  position_summary:
    '"Lateralus" ocupa el punto donde Tool convierte su lenguaje pesado y claustrofóbico en una forma mucho más abierta, ritual y ambiciosa. Se entiende mejor como una culminación del primer gran ciclo de la banda: el disco donde refinan su disciplina técnica y al mismo tiempo encuentran un sentido de expansión que ya estaba insinuado antes.',

  arch_type: 'Iniciación por umbrales',
  arch_summary:
    'La secuencia está construida como una serie de umbrales. Primero aprieta, luego desacelera, después encuentra una salida expansiva en el centro y finalmente entra en una zona de trance sostenido que deja de pensar en canciones individuales y empieza a pensar en estados.',
  arch_opening:
    '"The Grudge" abre como si el disco fuera a ser una versión agrandada y todavía más castigadora de "Ænima": percusión enorme, riffs serrados, voz en lucha abierta. "Eon Blue Apocalypse" y "The Patient" corrigen esa primera impresión enseguida, porque introducen pausa, espera y una sensación de proceso largo.',
  arch_middle:
    'El centro verdadero está entre "Schism" y "Lateralus". "Schism" plantea el problema en términos casi anatómicos, "Parabol" y "Parabola" reordenan la escala emocional del álbum, y "Ticks & Leeches" funciona como último espasmo de violencia reactiva antes de que "Lateralus" abra por fin la puerta grande.',
  arch_closing:
    'El cierre tiene dos capas. "Triad" lleva el trance a una forma instrumental y percutiva, casi de ceremonia sin palabras, y "Faaip de Oiad" entra después como una transmisión contaminada desde el borde de la cordura. En vez de dar resolución, el disco termina dejando una grieta abierta entre iluminación y paranoia.',
  arch_sequencing_note:
    'Los interludios cortos no son adorno. "Eon Blue Apocalypse" y "Mantra" funcionan como cámaras de descompresión antes de que el disco reoriente el cuerpo, y la secuencia "Disposition"/"Reflection"/"Triad" está colocada como una suite final que cambia el tiempo interno del álbum.',

  tensions: [
    {
      label: 'Querer trascender vs seguir aferrado al peso propio',
      description:
        'El disco vuelve una y otra vez a la idea de soltar, pero nunca lo plantea como un acto sereno. "The Grudge" convierte el rencor en motor físico, con riffs que aprietan y una voz que suena como alguien tratando de arrancarse algo del pecho. Luego "The Patient" introduce disciplina y demora, y "Disposition" recién ofrece una forma de calma que se siente trabajada, no dada.',
      anchors: [
        { track: 'The Grudge', note: 'rencor convertido en motor físico hasta el grito final' },
        {
          track: 'The Patient',
          note: '"I must keep reminding myself of this" como disciplina',
        },
        { track: 'Disposition', note: 'calma que llega trabajada, no regalada' },
      ],
    },
    {
      label: 'Análisis que divide vs experiencia que busca unirse',
      description:
        'Muchas canciones se construyen alrededor de piezas que quieren volver a encajar. "Schism" hace de la fractura su ritmo central. El díptico "Parabol"/"Parabola" propone una unión casi mística entre cuerpo y conciencia, mientras "Reflection" deja de perseguir resolución rápida y prefiere sostener la repetición hasta que la separación entre pensamiento, pulso y atmósfera empieza a borrarse.',
      anchors: [
        { track: 'Schism', note: 'obsesión rítmica con la separación' },
        {
          track: 'Parabol → Parabola',
          note: 'transición sin corte: contemplación cargada de impulso',
        },
        { track: 'Reflection', note: 'una sola idea sostenida hasta volverla estado' },
      ],
    },
    {
      label: 'Búsqueda espiritual vs necesidad de impacto físico',
      description:
        'Tool venía de un lenguaje pesado y abrasivo, y aquí no lo abandona. Lo que cambia es el uso de esa fuerza. "Ticks & Leeches" conserva la agresión más primitiva del grupo, pero "Lateralus" y "Triad" muestran que la intensidad puede abrir espacio en lugar de cerrarlo. El disco quiere elevarse y al mismo tiempo necesita seguir sintiéndose corporal, percusivo, casi muscular.',
      anchors: [
        { track: 'Ticks & Leeches', note: 'última embestida primitiva en el centro' },
        { track: 'Lateralus', note: 'intensidad que abre espacio en vez de cerrarlo' },
        { track: 'Triad', note: 'impulso instrumental casi tribal' },
      ],
    },
  ],

  signature_moments: [
    {
      track: 'Parabol / Parabola',
      anchor: 'la transición sin corte de "Parabol" a "Parabola"',
      pull: 'La quietud no llega para descansar, llega para cargar sentido antes de otra embestida. Cuando el golpe de entrada de "Parabola" cae, la banda convierte contemplación en impulso, y desde ahí se entiende que "Lateralus" busca expansión a través del cuerpo, no por fuga del cuerpo.',
      why: 'Ese paso explica cómo funciona todo el álbum como una iniciación física.',
    },
    {
      track: 'Lateralus',
      anchor: 'el estribillo cuando la canción se abre después de tanta contención métrica',
      pull: 'Ahí el disco deja de sentirse como una serie de pruebas y empieza a leerse como una invitación. La canción toma todas las obsesiones del álbum (patrón, cuenta, control, ascenso) y las convierte en sensación de apertura real.',
      why: 'Después de ese momento, el tramo final ya no suena como continuación de un álbum de metal, sino como su fase de trance.',
    },
  ],

  motifs: [
    'Lenguaje de separación y reunión: en "Schism", "Parabol", "Parabola" y "Reflection", la conciencia aparece partida y luego empujada hacia alguna forma de unión.',
    'Idea de ascenso, espiral o movimiento hacia afuera en "The Patient", "Parabola" y "Lateralus", siempre como esfuerzo activo más que como revelación caída del cielo.',
    'Referencias al cuerpo como límite y vehículo en "Parabol", "Parabola", "Ticks & Leeches" y "Lateralus", donde lo físico nunca queda atrás del todo.',
  ],

  enduring:
    '"Lateralus" sigue vivo porque encontró una manera rara de sonar enorme sin quedarse en la exhibición. Su grandeza no depende solo de tocar en compases raros o de construir canciones largas, sino de usar esa disciplina para cambiar la respiración del oyente. Pocos discos de metal de su escala consiguen que la exigencia técnica se sienta física y hasta meditativa a la vez. Por eso vuelve tanto: cada escucha deja ver mejor cómo convierte control extremo en apertura emocional real.',

  tracks: [
    { n: 1, title: 'The Grudge', ms: 516253, hasMeaning: true },
    { n: 2, title: 'Eon Blue Apocalypse', ms: 64733 },
    { n: 3, title: 'The Patient', ms: 433960 },
    { n: 4, title: 'Mantra', ms: 72672 },
    { n: 5, title: 'Schism', ms: 407000, hasMeaning: true },
    { n: 6, title: 'Parabol', ms: 184426 },
    { n: 7, title: 'Parabola', ms: 363533 },
    { n: 8, title: 'Ticks & Leeches', ms: 490000 },
    { n: 9, title: 'Lateralus', ms: 564146, hasMeaning: true, hinge: true },
    { n: 10, title: 'Disposition', ms: 286066 },
    { n: 11, title: 'Reflection', ms: 667925 },
    { n: 12, title: 'Triad', ms: 526000 },
    { n: 13, title: 'Faaip de Oiad', ms: 159000 },
  ],

  songs_with_meaning: ['The Grudge', 'Schism', 'Lateralus'],
};
